import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Check, AlertTriangle, Pill, Target, Activity, FileText, X } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { useFamily } from "../contexts/FamilyContext";

const ChronicProgramDetailContent = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { members, activeMember } = useFamily();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(false);
    const [selFmId, setSelFmId] = useState(null);
    const [enrollmentDetail, setEnrollmentDetail] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/chronic-programs/${slug}`);
            setData(data);
            // If user already has an enrollment for active member, fetch detail
            const enrollment = (data.enrollments || []).find((e) =>
                (e.family_member_id || null) === (activeMember?.is_self ? null : activeMember?.id || null)
            );
            if (enrollment) {
                const { data: detail } = await api.get(`/chronic-programs/enrollments/${enrollment.id}`);
                setEnrollmentDetail(detail);
            } else {
                setEnrollmentDetail(null);
            }
        } catch {
            toast.error("Could not load program");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug, activeMember?.id]);

    const onEnroll = async () => {
        try {
            setEnrolling(true);
            const fmId = selFmId !== null ? selFmId : (activeMember?.is_self ? null : activeMember?.id);
            await api.post(`/chronic-programs/${slug}/enroll`, { family_member_id: fmId });
            toast.success(`Enrolled in ${data.program.name}`);
            await load();
        } catch (err) {
            const det = err?.response?.data?.detail;
            if (det?.code === "ALREADY_ENROLLED") {
                toast.info("Already enrolled in this program");
                await load();
            } else {
                toast.error(typeof det === "string" ? det : "Could not enroll");
            }
        } finally {
            setEnrolling(false);
        }
    };

    const onUnenroll = async () => {
        if (!enrollmentDetail?.enrollment?.id) return;
        if (!window.confirm("Stop tracking this program?")) return;
        try {
            await api.post(`/chronic-programs/enrollments/${enrollmentDetail.enrollment.id}/unenroll`);
            toast.success("Unenrolled");
            setEnrollmentDetail(null);
            await load();
        } catch {
            toast.error("Could not unenroll");
        }
    };

    if (loading || !data) {
        return (
            <div className="space-y-4">
                <div className="h-12 w-44 skeleton rounded-xl" />
                <div className="h-48 skeleton rounded-3xl" />
                <div className="h-32 skeleton rounded-2xl" />
            </div>
        );
    }

    const p = data.program;
    const isEnrolled = !!enrollmentDetail;

    return (
        <div data-testid={`chronic-detail-${slug}`}>
            <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-brand-700 dark:text-zinc-400" data-testid="chronic-back-btn">
                <ChevronLeft className="h-4 w-4" /> Back
            </button>

            <div className={`relative overflow-hidden rounded-3xl border border-zinc-100 bg-gradient-to-br ${p.hero_color} p-8 dark:border-white/10 dark:from-brand-500/10 dark:to-brand-500/5`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="text-5xl">{p.icon_emoji}</span>
                            <div>
                                <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{p.name}</h1>
                                <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{p.tagline}</p>
                            </div>
                        </div>
                        {p.critical && (
                            <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-rose-800 dark:bg-rose-500/15 dark:text-rose-300">
                                <AlertTriangle className="h-3 w-3" /> Critical condition
                            </span>
                        )}
                    </div>

                    {isEnrolled ? (
                        <div className="flex flex-col items-end gap-2">
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" data-testid="chronic-enrolled-badge">
                                <Check className="h-3 w-3" /> Enrolled
                            </span>
                            <button onClick={onUnenroll} data-testid="chronic-unenroll-btn" className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-[11px] text-rose-700 hover:bg-rose-50 dark:border-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10">
                                <X className="h-3 w-3" /> Unenroll
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-end gap-2">
                            <select
                                value={selFmId === null ? "" : selFmId}
                                onChange={(e) => setSelFmId(e.target.value || null)}
                                data-testid="chronic-enroll-fm-select"
                                className="input-clean !py-1.5 !text-xs"
                            >
                                <option value="">Use active profile ({activeMember?.is_self ? "Me" : activeMember?.full_name || "—"})</option>
                                {members.filter((m) => !m.is_self).map((m) => (
                                    <option key={m.id} value={m.id}>{m.full_name}</option>
                                ))}
                            </select>
                            <button onClick={onEnroll} disabled={enrolling} className="btn-brand !py-2 !px-5 !text-sm" data-testid="chronic-enroll-btn">
                                {enrolling ? "Enrolling..." : "Enroll now"}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-3">
                <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft lg:col-span-2 dark:border-white/10 dark:bg-white/[0.03]">
                    <p className="label-uppercase">Why this program</p>
                    <ul className="mt-4 space-y-2">
                        {(p.benefits || []).map((b) => (
                            <li key={b} className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-700 dark:text-brand-400" /> {b}
                            </li>
                        ))}
                    </ul>

                    <p className="label-uppercase mt-6">Suggested medications</p>
                    <div className="mt-3 space-y-2">
                        {(p.suggested_medications || []).map((m) => (
                            <div key={m.name} className="flex flex-wrap items-center gap-2 rounded-2xl border border-zinc-100 bg-zinc-50/60 px-4 py-2.5 text-sm dark:border-white/5 dark:bg-white/[0.02]">
                                <Pill className="h-4 w-4 text-brand-700 dark:text-brand-400" />
                                <span className="font-medium text-zinc-900 dark:text-zinc-100">{m.name}</span>
                                <span className="text-xs text-zinc-500 dark:text-zinc-400">· {m.strength} · {m.type}</span>
                                <span className="ml-auto text-[11px] text-zinc-600 dark:text-zinc-400">{(m.default_slots || []).join(", ")}</span>
                            </div>
                        ))}
                    </div>
                    <Link to="/reminders/add" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline dark:text-brand-300" data-testid="chronic-add-med-cta">
                        + Add a reminder for one of these
                    </Link>
                </div>

                <div className="space-y-5">
                    <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-white/[0.03]">
                        <p className="label-uppercase inline-flex items-center gap-1">
                            <Activity className="h-3.5 w-3.5" /> Tracking targets
                        </p>
                        <div className="mt-3 space-y-2 text-sm">
                            {Object.entries(p.default_targets || {}).map(([k, v]) => (
                                <div key={k} className="flex flex-wrap items-baseline justify-between gap-1 rounded-xl bg-zinc-50/60 px-3 py-2 dark:bg-white/[0.02]">
                                    <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">{k.replace(/_/g, " ")}</span>
                                    <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
                                        {Object.entries(v).map(([kk, vv]) => `${kk}: ${vv}`).join(" · ")}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-[11px] text-zinc-500 dark:text-zinc-400">
                            <Target className="mr-1 inline h-3 w-3" /> Cadence: {p.measurement_cadence}
                        </p>
                    </div>

                    {isEnrolled && enrollmentDetail && (
                        <div className="rounded-3xl border border-brand-200 bg-brand-50/40 p-6 dark:border-brand-400/30 dark:bg-brand-500/5" data-testid="chronic-progress-card">
                            <p className="label-uppercase">Your progress</p>
                            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                                Recent measurements: <strong>{enrollmentDetail.recent_measurements?.length || 0}</strong> in last 30 days
                            </p>
                            <p className="text-sm text-zinc-700 dark:text-zinc-300">
                                Linked medications: <strong>{enrollmentDetail.related_medications?.length || 0}</strong>
                            </p>
                            <Link to="/health" className="mt-3 inline-flex items-center gap-1 rounded-full border border-brand-300 bg-white px-4 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-400/40 dark:bg-transparent dark:text-brand-300 dark:hover:bg-brand-500/10" data-testid="chronic-open-health">
                                <FileText className="h-3 w-3" /> Open health tracker
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ChronicProgramDetail = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <ChronicProgramDetailContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default ChronicProgramDetail;
